<template>
  <div class="fu-sum" data-test="fu-summary">
    <div class="fu-sum__card" :class="{ 'fu-sum__card--on': dueActive }" data-test="fu-summary-due">
      <div class="fu-sum__top">
        <span class="fu-sum__label">Due now</span>
        <span v-if="dueActive" class="fu-sum__tag fu-sum__tag--on">Filtering</span>
      </div>
      <div class="fu-sum__value">
        <span class="fu-sum__num" data-test="fu-summary-due-total">{{ dueTotal }}</span>
        <span class="fu-sum__unit">follow-ups</span>
      </div>
      <div class="fu-sum__legend">
        <span class="fu-sum__legend-item" data-test="fu-summary-due-today">
          <span class="fu-sum__dot fu-sum__dot--today" aria-hidden="true"></span>
          <strong>{{ dueToday }}</strong> due today
        </span>
        <span class="fu-sum__legend-item" data-test="fu-summary-past-due">
          <span class="fu-sum__dot fu-sum__dot--past" aria-hidden="true"></span>
          <strong>{{ pastDue }}</strong> past due
        </span>
      </div>
      <button
        type="button"
        class="fu-sum__show"
        :aria-pressed="dueActive ? 'true' : 'false'"
        data-test="fu-show-due"
        @click="$emit(dueActive ? 'show-all' : 'show-due')"
      >
        {{ dueActive ? 'Show everyone' : 'Show these' }}
        <fu-icon v-if="!dueActive" name="arrowR" :size="14" />
      </button>
    </div>

    <div
      v-for="g in goalCards"
      :key="g.key"
      class="fu-sum__card"
      :data-test="`fu-summary-goal-${g.key}`"
    >
      <div class="fu-sum__top">
        <span class="fu-sum__label">{{ g.label }}</span>
      </div>
      <div class="fu-sum__value">
        <span class="fu-sum__num" data-test="fu-goal-done">{{ g.done }}</span>
        <span class="fu-sum__unit" data-test="fu-goal-target">/ {{ g.goal || '—' }}</span>
      </div>
      <div
        class="fu-sum__bar"
        role="progressbar"
        :aria-label="`${g.label}: ${g.done} of ${g.goal || 'no goal set'}`"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="Math.round(g.pct)"
      >
        <div class="fu-sum__fill" :class="{ 'fu-sum__fill--done': g.reached }" :style="{ width: `${g.pct}%` }"></div>
      </div>
      <div v-if="g.message" class="fu-sum__sub" data-test="fu-goal-message">{{ g.message }}</div>
    </div>
  </div>
</template>

<script>
/**
 * The summary cards above the table — set A, "Work the list" (Nilay, 2026-10-02):
 *
 *   Due now   follow-ups due today + past due, from the rows the page has loaded. "Show these"
 *             emits `show-due` (the page turns the table's view chip to "Due now"); while that view
 *             is on, the card is outlined and the button emits `show-all`.
 *   Goals     one card per call goal — Daily / Weekly / Monthly — with the SAME numbers the old
 *             page shows: attempts / the goal setting. pageChrome.js goalProgress() turns each
 *             { done, goal } into the bar (capped at 100%) and the wording under it.
 *
 * Sets B and C (production estimate, benefits reset, how overdue) are not built — they need data
 * the pages do not have. Presentational: props in, events out.
 */
import FuIcon from './FuIcon.vue';
import { goalProgress } from '@/utils/followups/pageChrome.js';

const GOALS = [
  { key: 'daily', label: 'Daily goal', unit: 'calls to reach today’s goal' },
  { key: 'weekly', label: 'Weekly goal', unit: 'to go this week' },
  { key: 'monthly', label: 'Monthly goal', unit: 'to go this month' },
];

export default {
  name: 'FollowupsSummaryCards',
  components: { FuIcon },
  props: {
    dueToday: { type: Number, default: 0 },
    pastDue: { type: Number, default: 0 },
    dueActive: { type: Boolean, default: false },
    // { daily: { done, goal }, weekly: {…}, monthly: {…} } — a missing key draws no card.
    goals: { type: Object, default: () => ({}) },
  },
  computed: {
    dueTotal() {
      return (this.dueToday || 0) + (this.pastDue || 0);
    },
    goalCards() {
      return GOALS.filter(g => this.goals && this.goals[g.key]).map((g) => {
        const src = this.goals[g.key];
        const p = goalProgress(src.done, src.goal);
        let message = '';
        if (p.goal) message = p.reached ? 'Goal reached — nice work.' : `${p.remaining} more ${g.unit}`;
        return Object.assign({ key: g.key, label: g.label, message }, p);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-sum {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: $fu-space-4;
  margin-bottom: $fu-space-4;
}

.fu-sum__card {
  @include fu-card($fu-radius-lg);
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: $fu-space-4 18px;
}

.fu-sum__card--on {
  border-color: $fu-brand;
  box-shadow: $fu-ring;
}

.fu-sum__top {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  min-width: 0;
}

.fu-sum__label {
  @include fu-eyebrow($fu-text-muted);
  @include fu-ellipsis;
  min-width: 0;
}

.fu-sum__card--on .fu-sum__label {
  color: $fu-neutral-600;
}

.fu-sum__tag {
  margin-left: auto;
  font-size: 11.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-muted;
  white-space: nowrap;
}

.fu-sum__tag--on {
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
}

.fu-sum__value {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 10px;
  min-width: 0;
}

.fu-sum__num {
  @include fu-font-display;
  @include fu-tabular;
  font-weight: 800;
  font-size: 32px;
  line-height: 1;
  letter-spacing: -0.03em;
  white-space: nowrap;
}

.fu-sum__unit {
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-subtle;
  white-space: nowrap;
}

.fu-sum__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 14px;
  font-size: 12.5px;
  color: $fu-neutral-600;
}

.fu-sum__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  strong { font-weight: $fu-fw-bold; color: $fu-text; }
}

.fu-sum__dot {
  @include fu-status-dot;
}

.fu-sum__dot--today { background: $fu-brand; }
.fu-sum__dot--past { background: $fu-danger; }

.fu-sum__show {
  @include fu-link-btn;
  align-self: flex-start;
  margin-top: auto;
  padding: 10px 0 0;
  font-size: 13px;
}

.fu-sum__bar {
  height: 6px;
  margin-top: 14px;
  border-radius: 6px;
  background: $fu-neutral-100;
  overflow: hidden;
}

.fu-sum__fill {
  height: 100%;
  border-radius: 6px;
  background: $fu-brand;
  transition: width 0.3s ease;
}

.fu-sum__fill--done {
  background: $fu-success;
}

.fu-sum__sub {
  margin-top: 10px;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-text-muted;
}

@include fu-tablet {
  .fu-sum { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
}

@include fu-phone {
  .fu-sum { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .fu-sum__card { padding: 14px; }
  .fu-sum__num { font-size: 26px; }
}
</style>
